import { useId } from 'react';

const CONTROL_BASE =
  'w-full rounded-md border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-faint ' +
  'transition-[border-color,box-shadow] duration-150 ease-out';

const CONTROL_OK = 'border-line-strong hover:border-navy/40';
const CONTROL_ERR = 'border-danger';

/**
 * Shared control style for dense editor rows (profile, import review, resume
 * sections, version panel). Single source of truth so every text input,
 * textarea, and select stays visually identical instead of copying the class
 * string per component.
 */
export const controlClass =
  'w-full rounded-md border border-line-strong bg-surface px-2.5 py-1.5 text-sm text-ink ' +
  'transition-[border-color,box-shadow] duration-150 ease-out hover:border-navy/40';

interface FieldWrapProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: (ids: {
    id: string;
    hintId?: string | undefined;
    errorId?: string | undefined;
    describedBy?: string | undefined;
  }) => React.ReactNode;
}

/**
 * Shared label + hint + error scaffold. Inputs stay visually precise (a modest
 * radius, not a pill) while the surrounding chrome follows the product voice.
 */
function FieldWrap({ label, hint, error, children }: FieldWrapProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy =
    [hintId, errorId].filter((v): v is string => v !== undefined).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold text-ink">
        {label}
      </label>
      {hint ? (
        <p id={hintId} className="text-xs text-ink-faint">
          {hint}
        </p>
      ) : null}
      {children({ id, hintId, errorId, describedBy })}
      {error ? (
        <p id={errorId} role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  type?: 'text' | 'email' | 'tel' | 'url';
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  error,
  hint,
  type = 'text',
}: TextFieldProps) {
  return (
    <FieldWrap label={label} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <input
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
          className={`${CONTROL_BASE} ${error ? CONTROL_ERR : CONTROL_OK}`}
        />
      )}
    </FieldWrap>
  );
}

interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  rows?: number | undefined;
}

export function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  error,
  hint,
  rows = 10,
}: TextAreaFieldProps) {
  return (
    <FieldWrap label={label} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <textarea
          id={id}
          value={value}
          rows={rows}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
          className={`${CONTROL_BASE} resize-y leading-relaxed ${error ? CONTROL_ERR : CONTROL_OK}`}
        />
      )}
    </FieldWrap>
  );
}

/** Bare labelled input for dense editor rows (no hint/error chrome). */
export function InlineField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  className = '',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'url';
  className?: string;
}) {
  const id = useId();
  return (
    <label className={`flex flex-col gap-1 text-sm ${className}`} htmlFor={id}>
      <span className="font-medium text-ink-soft">{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`${CONTROL_BASE} ${CONTROL_OK}`}
      />
    </label>
  );
}
